import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function Reactivate() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("working"); // working | success | error
  const [error, setError] = useState("");
  const [role, setRole] = useState(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get("token");
    if (!token) {
      setStatus("error");
      setError("This reactivation link is missing its token.");
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/account/reactivate", { token });
        refreshUser(data.user);
        setRole(data.user?.role);
        setStatus("success");
      } catch (err) {
        setStatus("error");
        setError(formatApiErrorDetail(err.response?.data?.detail) || "We couldn't reactivate your account.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goHome = () => {
    if (role === "subscriber") navigate("/dashboard", { replace: true });
    else if (role === "admin") navigate("/admin", { replace: true });
    else navigate("/home", { replace: true });
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-24 text-center" data-testid="reactivate-page">
      {status === "working" && (
        <div data-testid="reactivate-working">
          <Loader2 className="w-10 h-10 mx-auto text-primary animate-spin" />
          <h1 className="font-display text-2xl font-semibold tracking-tight mt-6">Reactivating your account…</h1>
          <p className="text-muted-foreground mt-2">Hang tight, this only takes a moment.</p>
        </div>
      )}

      {status === "success" && (
        <div data-testid="reactivate-success">
          <CheckCircle2 className="w-12 h-12 mx-auto text-green-600" />
          <h1 className="font-display text-3xl font-semibold tracking-tighter mt-6">Welcome back!</h1>
          <p className="text-muted-foreground mt-2">
            Your account is active again — everything is exactly as you left it.
          </p>
          <Button onClick={goHome} className="rounded-sm mt-6" data-testid="reactivate-continue-btn">
            Go to my dashboard
          </Button>
        </div>
      )}

      {status === "error" && (
        <div data-testid="reactivate-error">
          <XCircle className="w-12 h-12 mx-auto text-red-600" />
          <h1 className="font-display text-3xl font-semibold tracking-tighter mt-6">Reactivation failed</h1>
          <p className="text-muted-foreground mt-2">{error}</p>
          <div className="flex items-center justify-center gap-3 mt-6">
            <Button asChild variant="outline" className="rounded-sm" data-testid="reactivate-login-btn">
              <Link to="/login">Go to login</Link>
            </Button>
            <Button asChild className="rounded-sm" data-testid="reactivate-support-btn">
              <Link to="/support">Contact support</Link>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
